// ═══════════════════════════════════════════════════════════════
// MainStreet — Contacts CRUD
// Table with org filter, modal for create/edit
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo, useRef } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Select, Switch,
  Textarea, ActionIcon, Modal, Stack, SimpleGrid, Pagination,
} from '../../components/gab/GabComponents';

const PAGE_SIZE = 25;
import {
  IconSearch, IconPlus, IconPencil, IconTrash, IconRefresh, IconUser, IconX,
} from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { useMainStreetStore } from '../../store/mainstreetStore';
import type { MSContact } from '../../store/mainstreetStore';
import { formatPhone } from '../../lib/phoneFormat';
import { useModulePermission } from '../../hooks/useModulePermission';
import { queryRecords } from '../../api/client';
import { TABLES } from '../../api/tables';
import { FIELDS } from '../../api/fields';

const CONTACT_TYPES = [
  { value: 'primary', label: 'Primary' },
  { value: 'emergency', label: 'Emergency' },
  { value: 'technical', label: 'Technical' },
  { value: 'management', label: 'Management' },
];

const EMPTY: Partial<MSContact> = {
  orgId: '', name: '', title: '', dept: '', type: '',
  email: '', phone: '', altPhone: '', canAuth: 'false', notes: '',
};

export default function Contacts() {
  const { canEdit } = useModulePermission('mainstreet');
  const {
    organizations, contacts, loadingContacts,
    loadOrganizations, loadContacts,
    createContact, updateContact, deleteContact,
  } = useMainStreetStore();

  const [search, setSearch] = useState('');
  const [orgFilter, setOrgFilter] = useState<string | null>(null);
  const [listFilter, setListFilter] = useState('all');
  const [sortField, setSortField] = useState<string>('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(1);
  const toggleSort = (field: string) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };
  const sortIcon = (f: string) => sortField === f ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

  const { orgLists, loadOrgLists } = useMainStreetStore();
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<MSContact | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<MSContact | null>(null);
  const [form, setForm] = useState<Partial<MSContact>>({ ...EMPTY });
  const [saving, setSaving] = useState(false);

  // Person picker state (only active when FIELDS.msContacts.personId is provisioned)
  const [linkedPersonId, setLinkedPersonId] = useState('');
  const [linkedPersonName, setLinkedPersonName] = useState('');
  const [personQuery, setPersonQuery] = useState('');
  const [personResults, setPersonResults] = useState<Array<{ id: string; name: string; title: string }>>([]);
  const [personSearching, setPersonSearching] = useState(false);
  const personDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handlePersonQuery = (q: string) => {
    setPersonQuery(q);
    if (personDebounce.current) clearTimeout(personDebounce.current);
    if (!q.trim() || !TABLES.people) { setPersonResults([]); return; }
    personDebounce.current = setTimeout(async () => {
      setPersonSearching(true);
      try {
        const FP = FIELDS.people;
        const res = await queryRecords<Record<string, unknown>>(TABLES.people, {
          filters: [{ field: FP.status, operator: 'eq', value: 'active' }],
          search: q,
          limit: 10,
        });
        setPersonResults(res.records.map(r => ({
          id: String(r.record_id),
          name: `${String(r[FP.firstName] ?? '')} ${String(r[FP.lastName] ?? '')}`.trim(),
          title: String(r[FP.title] ?? ''),
        })));
      } catch { /* ignore */ } finally { setPersonSearching(false); }
    }, 300);
  };

  useEffect(() => {
    loadOrganizations();
    loadContacts();
    loadOrgLists();
  }, []);

  const orgSelect = useMemo(() =>
    organizations.map(o => ({ value: String(o.id), label: o.name || `Org #${o.id}` })),
  [organizations]);

  const orgFilterSelect = useMemo(() =>
    [{ value: 'all', label: 'All Organizations' }, ...orgSelect],
  [orgSelect]);

  const listOrgIds = useMemo(() => {
    if (listFilter === 'all') return null;
    const l = orgLists.find(x => String(x.id) === listFilter);
    return l ? new Set((l.orgIds || '').split(',').filter(Boolean)) : null;
  }, [listFilter, orgLists]);

  const listFilterSelect = useMemo(() => [
    { value: 'all', label: 'All Lists' },
    ...orgLists.map(l => ({ value: String(l.id), label: `${l.name} (${(l.orgIds || '').split(',').filter(Boolean).length})` })),
  ], [orgLists]);

  const filtered = useMemo(() => {
    let list = contacts;
    if (listOrgIds) list = list.filter(c => listOrgIds.has(c.orgId));
    if (orgFilter && orgFilter !== 'all') list = list.filter(c => c.orgId === orgFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(c =>
        (c.name || '').toLowerCase().includes(q) ||
        (c.title || '').toLowerCase().includes(q) ||
        (c.dept || '').toLowerCase().includes(q) ||
        (c.email || '').toLowerCase().includes(q) ||
        (c.phone || '').includes(q),
      );
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      if (!sortField) return (a.name || '').localeCompare(b.name || '');
      const va = (a as any)[sortField] ?? '';
      const vb = (b as any)[sortField] ?? '';
      return String(va).localeCompare(String(vb)) * dir;
    });
  }, [contacts, orgFilter, search, listOrgIds, sortField, sortDir]);

  useEffect(() => { setPage(1); }, [orgFilter, listFilter, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const orgName = (id: string | number) => {
    if (!id) return '—';
    const sid = String(id);
    const org = organizations.find(o => String(o.id) === sid || String(o.Rid) === sid);
    return org?.name || org?.orgName || `Org #${id}`;
  };

  const setField = (key: keyof MSContact, value: string) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const openCreate = () => {
    setEditTarget(null);
    setForm({ ...EMPTY });
    setLinkedPersonId('');
    setLinkedPersonName('');
    setPersonQuery('');
    setPersonResults([]);
    setModalOpen(true);
  };

  const openEdit = (contact: MSContact) => {
    setEditTarget(contact);
    setForm({ ...contact });
    setLinkedPersonId(contact.personId || '');
    setLinkedPersonName(contact.personId ? (contact as any)._personName || '' : '');
    setPersonQuery('');
    setPersonResults([]);
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.name?.trim()) {
      notifications.show({ message: 'Contact name is required', color: '#C68700' });
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, ...(FIELDS.msContacts.personId ? { personId: linkedPersonId } : {}) };
      if (editTarget) {
        await updateContact(editTarget.id, payload);
        notifications.show({ message: 'Contact updated', color: 'var(--color-success)' });
      } else {
        await createContact(payload);
        notifications.show({ message: 'Contact created', color: 'var(--color-success)' });
      }
      setModalOpen(false);
      setEditTarget(null);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Save failed', color: 'var(--color-error)' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await deleteContact(deleteTarget.id);
      notifications.show({ message: `${deleteTarget.name} deleted`, color: '#939598' });
      setDeleteTarget(null);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Delete failed', color: 'var(--color-error)' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Contacts</Title>
        <Group gap="xs">
          <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />}
            loading={loadingContacts} onClick={() => loadContacts()}>Refresh</Button>
          {canEdit && <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>Add Contact</Button>}
        </Group>
      </Group>

      <Group mb="md" gap="sm">
        <TextInput placeholder="Search name, title, dept, email, phone..."
          leftSection={<IconSearch size={14} />} value={search}
          onChange={e => setSearch(e.target.value)} size="xs"
          style={{ flex: 1, minWidth: 200 }} />
        <Select data={orgFilterSelect} value={orgFilter ?? 'all'}
          onChange={v => setOrgFilter(v)} size="xs" w={220} />
        {orgLists.length > 0 && (
          <Select data={listFilterSelect} value={listFilter}
            onChange={v => setListFilter(v ?? 'all')} size="xs" w={200} />
        )}
      </Group>

      <Text size="xs" c="dimmed" mb="xs">{filtered.length} contact{filtered.length !== 1 ? 's' : ''}{filtered.length > PAGE_SIZE ? ` — page ${page} of ${totalPages}` : ''}</Text>

      <Paper withBorder>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #cbd5e1' }}>
                <th style={{ padding: '8px 16px', textAlign: 'left', cursor: 'pointer' }} onClick={() => toggleSort('name')}>Name{sortIcon('name')}</th>
                <th style={{ padding: '8px 16px', textAlign: 'left', cursor: 'pointer' }} onClick={() => toggleSort('orgId')}>Organization{sortIcon('orgId')}</th>
                <th style={{ padding: '8px 16px', textAlign: 'left', cursor: 'pointer' }} onClick={() => toggleSort('title')}>Title{sortIcon('title')}</th>
                <th style={{ padding: '8px 16px', textAlign: 'left', cursor: 'pointer' }} onClick={() => toggleSort('dept')}>Dept{sortIcon('dept')}</th>
                <th style={{ padding: '8px 16px', textAlign: 'left', cursor: 'pointer' }} onClick={() => toggleSort('contactType')}>Type{sortIcon('contactType')}</th>
                <th style={{ padding: '8px 16px', textAlign: 'left' }}>Email</th>
                <th style={{ padding: '8px 16px', textAlign: 'left' }}>Phone</th>
                <th style={{ padding: '8px 16px', textAlign: 'center' }}>Auth</th>
                <th style={{ padding: '8px 16px', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={9} style={{ padding: 24, textAlign: 'center', color: '#888' }}>
                    {loadingContacts ? 'Loading...' : 'No contacts found'}
                  </td>
                </tr>
              )}
              {paged.map(contact => (
                <tr key={contact.id} style={{ borderBottom: '1px solid #e2e8f0', cursor: 'pointer' }} onClick={() => openEdit(contact)}>
                  <td style={{ padding: '8px 16px', fontWeight: 500 }}>{contact.name || '-'}</td>
                  <td style={{ padding: '8px 16px' }}>{orgName(contact.orgId)}</td>
                  <td style={{ padding: '8px 16px' }}>{contact.title || '-'}</td>
                  <td style={{ padding: '8px 16px' }}>{contact.dept || '-'}</td>
                  <td style={{ padding: '8px 16px' }}>
                    {contact.type && <Badge size="xs" variant="light">{contact.type}</Badge>}
                  </td>
                  <td style={{ padding: '8px 16px' }}>{contact.email || '-'}</td>
                  <td style={{ padding: '8px 16px' }}>{contact.phone ? formatPhone(contact.phone) : '-'}</td>
                  <td style={{ padding: '8px 16px', textAlign: 'center' }}>
                    <Badge size="xs" color={contact.canAuth === 'true' ? 'var(--color-success)' : '#939598'}>
                      {contact.canAuth === 'true' ? 'Yes' : '-'}
                    </Badge>
                  </td>
                  <td style={{ padding: '8px 16px', textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                    {canEdit && (
                      <Group gap={4} justify="flex-end">
                        <ActionIcon size="sm" variant="subtle" onClick={() => openEdit(contact)}>
                          <IconPencil size={14} />
                        </ActionIcon>
                        <ActionIcon size="sm" variant="subtle" color="var(--color-error)" onClick={() => setDeleteTarget(contact)}>
                          <IconTrash size={14} />
                        </ActionIcon>
                      </Group>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Paper>

      {totalPages > 1 && (
        <Group justify="center" mt="md">
          <Pagination total={totalPages} value={page} onChange={setPage} size="sm" />
        </Group>
      )}

      {/* Create / Edit Modal */}
      <Modal opened={modalOpen} onClose={() => setModalOpen(false)}
        title={editTarget ? 'Edit Contact' : 'Add Contact'} centered size="lg">
        <Stack>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <Select label="Organization" data={orgSelect} value={form.orgId ? String(form.orgId) : null}
              onChange={v => setField('orgId', v ?? '')} searchable clearable />
            <TextInput label="Name" required value={form.name ?? ''}
              onChange={e => setField('name', e.target.value)} />
            <TextInput label="Title" value={form.title ?? ''}
              onChange={e => setField('title', e.target.value)} />
            <TextInput label="Department" value={form.dept ?? ''}
              onChange={e => setField('dept', e.target.value)} />
            <Select label="Type" data={CONTACT_TYPES} value={form.type || null}
              onChange={v => setField('type', v ?? '')} clearable />
            <TextInput label="Email" value={form.email ?? ''}
              onChange={e => setField('email', e.target.value)} />
            <TextInput label="Phone" value={form.phone ?? ''}
              onChange={e => setField('phone', e.target.value)} />
            <TextInput label="Alt Phone" value={form.altPhone ?? ''}
              onChange={e => setField('altPhone', e.target.value)} />
          </SimpleGrid>
          <Switch label="Can Authorize" checked={form.canAuth === 'true'}
            onChange={e => setField('canAuth', e.currentTarget.checked ? 'true' : 'false')} />
          <Textarea label="Notes" value={form.notes ?? ''}
            onChange={e => setField('notes', e.target.value)} autosize minRows={2} />

          {/* Directory Person link — only shown when personId field is provisioned in GAB */}
          {FIELDS.msContacts.personId && TABLES.people && (
            <div>
              <Text size="sm" fw={500} mb={4}>
                <IconUser size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                Linked Directory Person (optional)
              </Text>
              {linkedPersonId ? (
                <Group gap="xs">
                  <Badge
                    size="sm"
                    leftSection={<IconUser size={10} />}
                    rightSection={
                      <IconX size={10} style={{ cursor: 'pointer' }} onClick={() => { setLinkedPersonId(''); setLinkedPersonName(''); }} />
                    }
                  >
                    {linkedPersonName || `Person #${linkedPersonId}`}
                  </Badge>
                </Group>
              ) : (
                <div style={{ position: 'relative' }}>
                  <TextInput
                    size="sm"
                    placeholder="Search by name to link a directory person..."
                    leftSection={<IconSearch size={14} />}
                    value={personQuery}
                    onChange={e => handlePersonQuery(e.target.value)}
                  />
                  {(personResults.length > 0 || personSearching) && (
                    <Paper withBorder style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 300, maxHeight: 180, overflowY: 'auto' }}>
                      {personSearching ? (
                        <Text size="xs" c="dimmed" p="xs">Searching...</Text>
                      ) : personResults.map(p => (
                        <div key={p.id} style={{ padding: '8px 8px', cursor: 'pointer' }}
                          onMouseDown={() => { setLinkedPersonId(p.id); setLinkedPersonName(p.name); setPersonQuery(''); setPersonResults([]); }}>
                          <Text size="sm" fw={500}>{p.name}</Text>
                          {p.title && <Text size="xs" c="dimmed">{p.title}</Text>}
                        </div>
                      ))}
                    </Paper>
                  )}
                </div>
              )}
            </div>
          )}

          <Group justify="flex-end">
            <Button variant="default" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button loading={saving} onClick={handleSave}>
              {editTarget ? 'Save Changes' : 'Create Contact'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Delete confirmation */}
      <Modal opened={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Delete Contact" centered size="sm">
        <Stack>
          <Text size="sm">Are you sure you want to delete <strong>{deleteTarget?.name}</strong>?</Text>
          <Group justify="flex-end">
            <Button variant="default" size="xs" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button color="var(--color-error)" size="xs" loading={saving} onClick={handleDelete}>Delete</Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
}
